import { useState, useEffect } from "react";
import { supabase, EventPopup } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import RichTextEditor from "../../../components/base/RichTextEditor";

type Lang = "vi" | "kr";
const t = (lang: Lang, vi: string, kr: string) => (lang === "vi" ? vi : kr);

const DESIGN_TYPES = [
  { value: "standard", label: "Standard – Tiêu chuẩn" },
  { value: "announcement", label: "Announcement – Thông báo" },
  { value: "sale", label: "Sale / Khuyến mãi" },
  { value: "event", label: "Event – Sự kiện lớn" },
];

const emptyPopup = (): Partial<EventPopup> => ({
  id: "",
  title_vi: "",
  title_kr: "",
  description_vi: "",
  description_kr: "",
  image_url: "",
  button_text_vi: "Đăng ký tham gia",
  button_text_kr: "참여 신청",
  button_link: "",
  bg_color: "#004A99",
  is_active: true,
  show_delay_seconds: 2,
  show_from: "",
  show_until: "",
  design_type: "standard",
  event_date: "",
  event_location_vi: "",
  event_location_kr: "",
  highlights: [],
});

interface RegistrationRow {
  id: string;
  event_id: string;
  event_name: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  source: string;
  created_at: string;
}

interface Props {
  lang: Lang;
}

const EventPopupsManager = ({ lang }: Props) => {
  const [mainTab, setMainTab] = useState<"popups" | "registrations">("popups");
  const [popups, setPopups] = useState<EventPopup[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<EventPopup> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [highlightInput, setHighlightInput] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>([]);

  // Registrations state
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regSearch, setRegSearch] = useState("");
  const [regEventFilter, setRegEventFilter] = useState("");

  const fetchPopups = async () => {
    setLoading(true);
    const { data } = await supabase.from("event_popups").select("*").order("created_at", { ascending: false });
    setPopups((data as EventPopup[]) ?? []);
    setLoading(false);
  };

  const fetchRegistrations = async () => {
    setRegLoading(true);
    const { data } = await supabase
      .from("event_registrations")
      .select("*")
      .order("created_at", { ascending: false });
    setRegistrations((data as RegistrationRow[]) ?? []);
    setRegLoading(false);
  };

  useEffect(() => { fetchPopups(); }, []);

  useEffect(() => {
    if (mainTab === "registrations") fetchRegistrations();
  }, [mainTab]);

  const exportRegistrationsToExcel = () => {
    const filtered = filteredRegistrations;
    const headers = ["STT", "Sự kiện", "Họ tên", "Email", "Điện thoại", "Nội dung", "Nguồn", "Ngày đăng ký"];
    const rows = filtered.map((r, i) => [
      i + 1,
      r.event_name ?? "",
      r.name ?? "",
      r.email ?? "",
      r.phone ?? "",
      r.message ?? "",
      r.source ?? "",
      r.created_at ? new Date(r.created_at).toLocaleString("vi-VN") : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dang-ky-su-kien-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredRegistrations = registrations.filter((r) => {
    const matchEvent = !regEventFilter || r.event_id === regEventFilter;
    const q = regSearch.toLowerCase();
    const matchSearch = !q || r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.phone?.includes(q);
    return matchEvent && matchSearch;
  });

  const openNew = () => {
    setEditing(emptyPopup());
    setImages([]);
    setHighlightInput("");
    setError("");
  };

  const openEdit = (p: EventPopup) => {
    setEditing({ ...p });
    setImages(p.image_url ? [p.image_url] : []);
    setHighlightInput("");
    setError("");
  };

  const set = (field: keyof EventPopup, val: unknown) =>
    setEditing((prev) => prev ? { ...prev, [field]: val } : prev);

  const addHighlight = () => {
    if (!highlightInput.trim()) return;
    const existing = (editing?.highlights ?? []) as string[];
    set("highlights", [...existing, highlightInput.trim()]);
    setHighlightInput("");
  };

  const removeHighlight = (i: number) => {
    const existing = [...((editing?.highlights ?? []) as string[])];
    existing.splice(i, 1);
    set("highlights", existing);
  };

  const handleSave = async () => {
    if (!editing?.title_vi?.trim()) {
      setError(t(lang, "Vui lòng nhập tiêu đề!", "제목을 입력하세요!"));
      return;
    }
    if (!editing.id?.trim()) {
      const autoId = `popup-${Date.now()}`;
      setEditing(prev => prev ? { ...prev, id: autoId } : prev);
      editing.id = autoId;
    }
    setSaving(true);
    setError("");
    const payload: EventPopup = {
      ...editing as EventPopup,
      image_url: images[0] ?? editing.image_url ?? "",
      updated_at: new Date().toISOString(),
    };
    const { error: dbErr } = await supabase
      .from("event_popups")
      .upsert(payload, { onConflict: "id" });
    setSaving(false);
    if (dbErr) { setError(dbErr.message); return; }
    setEditing(null);
    fetchPopups();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("event_popups").delete().eq("id", id);
    setDeleteConfirm(null);
    fetchPopups();
  };

  const toggleActive = async (p: EventPopup) => {
    await supabase.from("event_popups").update({ is_active: !p.is_active }).eq("id", p.id);
    fetchPopups();
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t(lang, "Quản Lý Popup Sự Kiện", "이벤트 팝업 관리")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t(lang, "Tạo và quản lý các popup hiện trên trang chủ", "메인 페이지에 표시되는 팝업 관리")}</p>
        </div>
        {mainTab === "popups" && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-xl hover:bg-[#003a7a] cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-add-line" />
            {t(lang, "Tạo Popup Mới", "새 팝업 만들기")}
          </button>
        )}
        {mainTab === "registrations" && (
          <button
            onClick={exportRegistrationsToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-file-excel-line" />
            {t(lang, "Xuất Excel", "엑셀 내보내기")}
          </button>
        )}
      </div>

      {/* Main tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        <button
          onClick={() => setMainTab("popups")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mainTab === "popups" ? "bg-white text-[#004A99]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-notification-3-line" />
          {t(lang, "Popup sự kiện", "이벤트 팝업")}
          <span className="text-[10px] bg-[#004A99]/10 text-[#004A99] px-1.5 py-0.5 rounded-full">{popups.length}</span>
        </button>
        <button
          onClick={() => setMainTab("registrations")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mainTab === "registrations" ? "bg-white text-[#004A99]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-user-add-line" />
          {t(lang, "Danh sách đăng ký", "등록 목록")}
          {registrations.length > 0 && (
            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">{registrations.length}</span>
          )}
        </button>
      </div>

      {/* ── TAB: POPUPS ── */}
      {mainTab === "popups" && (
        <>
          {loading ? (
            <div className="text-center py-20"><i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" /></div>
          ) : popups.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
              <i className="ri-notification-3-line text-4xl text-gray-200 block mb-4" />
              <p className="text-gray-500 text-sm">{t(lang, "Chưa có popup nào. Tạo popup đầu tiên!", "팝업이 없습니다. 첫 번째 팝업을 만드세요!")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {popups.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-gray-200 p-5 flex items-start gap-5">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title_vi} className="w-24 h-20 object-cover rounded-xl flex-shrink-0" />
                  ) : (
                    <div className="w-24 h-20 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: p.bg_color ?? "#004A99" }}>
                      <i className="ri-notification-3-line text-white text-2xl" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 text-sm truncate">{p.title_vi}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                        {p.is_active ? t(lang, "Đang hiện", "표시 중") : t(lang, "Tắt", "비활성")}
                      </span>
                      <span className="text-[10px] bg-[#EDF2FB] text-[#004A99] px-2 py-0.5 rounded-full font-medium">{p.design_type}</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-2 line-clamp-1">{p.description_vi}</p>
                    <div className="flex items-center gap-3 text-[10px] text-gray-400">
                      {p.event_date && <span><i className="ri-calendar-line mr-1" />{p.event_date}</span>}
                      {p.event_location_vi && <span><i className="ri-map-pin-line mr-1" />{p.event_location_vi}</span>}
                      <span><i className="ri-time-line mr-1" />{t(lang, "Trễ", "지연")} {p.show_delay_seconds}s</span>
                      {/* Registration count */}
                      {registrations.filter(r => r.event_id === p.id).length > 0 && (
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                          <i className="ri-user-add-line mr-1" />
                          {registrations.filter(r => r.event_id === p.id).length} {t(lang, "đăng ký", "등록")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleActive(p)}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium cursor-pointer transition-colors whitespace-nowrap ${p.is_active ? "bg-gray-100 text-gray-600 hover:bg-gray-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                    >
                      {p.is_active ? t(lang, "Tắt", "비활성") : t(lang, "Bật", "활성")}
                    </button>
                    <button onClick={() => openEdit(p)} className="p-2 hover:bg-[#EDF2FB] rounded-lg cursor-pointer transition-colors">
                      <i className="ri-edit-line text-[#004A99]" />
                    </button>
                    <button onClick={() => setDeleteConfirm(p.id)} className="p-2 hover:bg-red-50 rounded-lg cursor-pointer transition-colors">
                      <i className="ri-delete-bin-line text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── TAB: REGISTRATIONS ── */}
      {mainTab === "registrations" && (
        <div>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="w-8 h-8 bg-[#004A99] rounded-lg flex items-center justify-center mb-2">
                <i className="ri-user-add-line text-white text-sm" />
              </div>
              <p className="text-xl font-black text-gray-800">{registrations.length}</p>
              <p className="text-xs text-gray-400">{t(lang, "Tổng đăng ký", "총 등록")}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mb-2">
                <i className="ri-calendar-event-line text-white text-sm" />
              </div>
              <p className="text-xl font-black text-gray-800">{new Set(registrations.map(r => r.event_id)).size}</p>
              <p className="text-xs text-gray-400">{t(lang, "Sự kiện có đăng ký", "등록된 이벤트")}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mb-2">
                <i className="ri-mail-line text-white text-sm" />
              </div>
              <p className="text-xl font-black text-gray-800">{registrations.filter(r => r.email).length}</p>
              <p className="text-xs text-gray-400">{t(lang, "Có email", "이메일 있음")}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder={t(lang, "Tìm theo tên, email, SĐT...", "이름, 이메일, 전화로 검색...")}
                value={regSearch}
                onChange={(e) => setRegSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#004A99]"
              />
            </div>
            <select
              value={regEventFilter}
              onChange={(e) => setRegEventFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#004A99] bg-white"
            >
              <option value="">{t(lang, "Tất cả sự kiện", "모든 이벤트")}</option>
              {popups.map(p => (
                <option key={p.id} value={p.id}>{p.title_vi}</option>
              ))}
            </select>
          </div>

          {regLoading ? (
            <div className="text-center py-20"><i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" /></div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
              <i className="ri-user-add-line text-4xl text-gray-200 block mb-4" />
              <p className="text-gray-400 text-sm">{t(lang, "Chưa có đăng ký nào", "등록이 없습니다")}</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t(lang, "Sự kiện", "이벤트")}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t(lang, "Người đăng ký", "등록자")}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t(lang, "Liên hệ", "연락처")}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t(lang, "Nguồn", "출처")}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{t(lang, "Ngày đăng ký", "등록일")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredRegistrations.map((r, idx) => (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-xs text-gray-400">{idx + 1}</td>
                      <td className="px-4 py-3 max-w-[180px]">
                        <p className="text-xs font-semibold text-gray-800 line-clamp-2">{r.event_name || "—"}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{r.name || "—"}</p>
                        {r.message && <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{r.message}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-700">{r.email || "—"}</p>
                        <p className="text-xs text-gray-400">{r.phone || ""}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${r.source === "popup" ? "bg-[#EDF2FB] text-[#004A99]" : "bg-amber-50 text-amber-700"}`}>
                          {r.source === "popup" ? "Popup" : "Trang chi tiết"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-400 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString("vi-VN") : "—"}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-bold text-gray-900 text-center mb-2">{t(lang, "Xóa popup này?", "이 팝업을 삭제할까요?")}</h3>
            <p className="text-gray-500 text-sm text-center mb-5">{t(lang, "Hành động này không thể hoàn tác.", "이 작업은 되돌릴 수 없습니다.")}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50">{t(lang, "Hủy", "취소")}</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-red-600">{t(lang, "Xóa", "삭제")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-bold text-gray-800">{editing.id && popups.find(p => p.id === editing.id) ? t(lang, "Chỉnh sửa Popup", "팝업 수정") : t(lang, "Tạo Popup Mới", "새 팝업")}</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
            </div>

            <div className="p-6 space-y-4">
              {error && <div className="bg-red-50 text-red-700 text-xs rounded-lg px-3 py-2 border border-red-200">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <div className="hidden">
                  <label className={labelCls}>ID Popup *</label>
                  <input className={inputCls} placeholder="summer-event-2026" value={editing.id ?? ""} onChange={(e) => set("id", e.target.value)} disabled={!!popups.find(p => p.id === editing.id)} />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Kiểu thiết kế", "디자인 유형")}</label>
                  <select className={inputCls} value={editing.design_type ?? "standard"} onChange={(e) => set("design_type", e.target.value)}>
                    {DESIGN_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Tiêu đề (Tiếng Việt) *", "제목 (베트남어) *")}</label>
                  <input className={inputCls} value={editing.title_vi ?? ""} onChange={(e) => set("title_vi", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Tiêu đề (Tiếng Hàn)", "제목 (한국어)")}</label>
                  <input className={inputCls} value={editing.title_kr ?? ""} onChange={(e) => set("title_kr", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Mô tả ngắn (VI) — hiện trong popup", "짧은 설명 (VI) — 팝업에 표시")}</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={2}
                    maxLength={200}
                    placeholder={t(lang, "Tóm tắt ngắn gọn về sự kiện...", "이벤트 간단 요약...")}
                    value={(editing as Record<string, unknown>).short_description_vi as string ?? ""}
                    onChange={(e) => setEditing((prev) => prev ? { ...prev, short_description_vi: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Mô tả ngắn (KR)", "짧은 설명 (KR)")}</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={2}
                    maxLength={200}
                    placeholder={t(lang, "Tóm tắt ngắn gọn (tiếng Hàn)...", "이벤트 간단 요약 (한국어)...")}
                    value={(editing as Record<string, unknown>).short_description_kr as string ?? ""}
                    onChange={(e) => setEditing((prev) => prev ? { ...prev, short_description_kr: e.target.value } : prev)}
                  />
                </div>
              </div>

              <div>
                <RichTextEditor
                  value={editing.description_vi ?? ""}
                  onChange={(html) => set("description_vi", html)}
                  placeholder={t(lang, "Nhập nội dung chi tiết sự kiện (tiếng Việt)...", "이벤트 상세 내용 입력 (베트남어)...")}
                  minHeight={120}
                  folder="events"
                  label={t(lang, "Nội dung chi tiết (VI)", "상세 내용 (VI)")}
                />
              </div>
              <div>
                <RichTextEditor
                  value={editing.description_kr ?? ""}
                  onChange={(html) => set("description_kr", html)}
                  placeholder={t(lang, "Nhập nội dung chi tiết sự kiện (tiếng Hàn)...", "이벤트 상세 내용 입력 (한국어)...")}
                  minHeight={120}
                  folder="events"
                  label={t(lang, "Nội dung chi tiết (KR)", "상세 내용 (KR)")}
                />
              </div>

              <ImageUploader
                images={images}
                onChange={(urls) => { setImages(urls); set("image_url", urls[0] ?? ""); }}
                maxImages={1}
                folder="events"
                label={t(lang, "Ảnh popup (khi có ảnh, màu nền sẽ ẩn đi)", "팝업 이미지 (이미지가 있으면 배경색 숨김)")}
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Ngày sự kiện", "행사 날짜")}</label>
                  <input className={inputCls} placeholder="15/04/2026" value={editing.event_date ?? ""} onChange={(e) => set("event_date", e.target.value)} />
                </div>
                {!images[0] && (
                  <div>
                    <label className={labelCls}>{t(lang, "Màu nền (chỉ dùng khi không có ảnh)", "배경 색상 (이미지 없을 때만)")}</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={editing.bg_color ?? "#004A99"} onChange={(e) => set("bg_color", e.target.value)} className="w-9 h-9 rounded border cursor-pointer" />
                      <input className={`${inputCls} flex-1`} value={editing.bg_color ?? "#004A99"} onChange={(e) => set("bg_color", e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Địa điểm (VI)", "장소 (VI)")}</label>
                  <input className={inputCls} value={editing.event_location_vi ?? ""} onChange={(e) => set("event_location_vi", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Địa điểm (KR)", "장소 (KR)")}</label>
                  <input className={inputCls} value={editing.event_location_kr ?? ""} onChange={(e) => set("event_location_kr", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Nút CTA (VI)", "버튼 (VI)")}</label>
                  <input className={inputCls} value={editing.button_text_vi ?? ""} onChange={(e) => set("button_text_vi", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Nút CTA (KR)", "버튼 (KR)")}</label>
                  <input className={inputCls} value={editing.button_text_kr ?? ""} onChange={(e) => set("button_text_kr", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Link nút CTA", "버튼 링크")}</label>
                  <input className={inputCls} placeholder="https://..." value={editing.button_link ?? ""} onChange={(e) => set("button_link", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Hiện sau (giây)", "표시 지연 (초)")}</label>
                  <input type="number" className={inputCls} min={0} max={30} value={editing.show_delay_seconds ?? 2} onChange={(e) => set("show_delay_seconds", parseInt(e.target.value))} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t(lang, "Hiện từ (tùy chọn)", "표시 시작 (선택)")}</label>
                  <input type="datetime-local" className={inputCls} value={(editing.show_from ?? "").slice(0, 16)} onChange={(e) => set("show_from", e.target.value || null)} />
                </div>
                <div>
                  <label className={labelCls}>{t(lang, "Hiện đến (tùy chọn)", "표시 종료 (선택)")}</label>
                  <input type="datetime-local" className={inputCls} value={(editing.show_until ?? "").slice(0, 16)} onChange={(e) => set("show_until", e.target.value || null)} />
                </div>
              </div>

              <div>
                <label className={labelCls}>{t(lang, "Điểm nổi bật (danh sách)", "주요 내용 목록")}</label>
                <div className="flex gap-2 mb-2">
                  <input
                    className={`${inputCls} flex-1`}
                    placeholder={t(lang, "Nhập điểm nổi bật...", "주요 내용 입력...")}
                    value={highlightInput}
                    onChange={(e) => setHighlightInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHighlight(); }}}
                  />
                  <button type="button" onClick={addHighlight} className="px-3 py-2 bg-[#004A99] text-white rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-[#003a7a]">{t(lang, "Thêm", "추가")}</button>
                </div>
                <div className="space-y-1.5">
                  {((editing.highlights ?? []) as string[]).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#F0F6FF] rounded-lg px-3 py-2">
                      <i className="ri-checkbox-circle-line text-[#004A99] text-sm flex-shrink-0" />
                      <span className="text-sm text-gray-700 flex-1">{h}</span>
                      <button onClick={() => removeHighlight(i)} className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors"><i className="ri-close-line text-sm" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input type="checkbox" id="is_active" checked={editing.is_active ?? true} onChange={(e) => set("is_active", e.target.checked)} className="w-4 h-4" />
                <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 cursor-pointer">{t(lang, "Hiển thị popup này trên website", "웹사이트에 이 팝업 표시")}</label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">{t(lang, "Hủy", "취소")}</button>
              <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2">
                {saving && <i className="ri-loader-4-line animate-spin" />}
                {t(lang, "Lưu Popup", "저장")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPopupsManager;
